import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDownIcon, MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Logo } from './Logo';
import { ServiceIcon } from './ServiceIcon';
import { navLinks, site } from '../data/site';
import { serviceLinks } from '../data/services';
const ease = [0.23, 1, 0.32, 1] as const;
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname, location.hash]);
  useEffect(() => {
    if (!servicesOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setServicesOpen(false);
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setServicesOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [servicesOpen]);
  const linkClass = 'font-display text-lg font-semibold uppercase tracking-wide text-white/90 transition-colors duration-150 hover:text-white';
  return <header className={`sticky top-0 z-40 w-full bg-brand transition-shadow duration-200 ${scrolled ? 'shadow-[0_6px_24px_rgba(0,40,70,0.25)]' : ''}`} data-id="element-175" data-mp="1y0m6jc7i5xu">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-5 lg:px-8" data-id="element-176" data-mp="1remfknrws8ia">
        <Logo data-id="element-177" data-mp="1wbml8xkuj98v" />

        <nav aria-label="Main" className="hidden items-center gap-6 lg:flex xl:gap-8" data-id="element-178" data-mp="1wc4r8lxb91tz">
          <div ref={menuRef} className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)} data-id="element-179" data-mp="1hmyrmwuk99lk">
            <button type="button" onClick={() => setServicesOpen(v => !v)} aria-expanded={servicesOpen} aria-controls="services-menu" className={`flex items-center gap-1 ${linkClass}`} data-id="element-180" data-mp="18maes5gwlfa9">
              Services
              <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ${servicesOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-181" data-mp="13p1x05zo3d9j" />
            </button>
            <AnimatePresence data-id="element-182" data-mp="2y0ovq3lojxv7">
              {servicesOpen && <motion.div id="services-menu" initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.16,
              ease
            }} className="absolute left-1/2 top-full w-[600px] -translate-x-1/2 pt-4" data-id="element-183" data-mp="me4zolttcfnr">
                  <div className="rounded-2xl bg-white p-3 shadow-2xl ring-1 ring-line" data-id="element-184" data-mp="1555wb37gnz45">
                    <ul className="grid grid-cols-2 gap-0.5" data-id="element-185" data-mp="156z3qkpwend7">
                      {serviceLinks.map(s => <li key={s.slug} data-id="element-186" data-mp="1f3bzdo507bal">
                          <NavLink to={`/${s.slug}`} className={({
                      isActive
                    }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 font-semibold transition-colors duration-150 ${isActive ? 'bg-brand-light text-brand-deep' : 'text-ink hover:bg-canvas'}`} data-id="element-187" data-mp="1f20tnirzztgu">
                            <ServiceIcon name={s.icon} className="h-5 w-5 shrink-0 text-brand" data-id="element-188" data-mp="27xi5qsf7qs0i" />
                            {s.name}
                          </NavLink>
                        </li>)}
                    </ul>
                    <Link to="/#services" className="mt-2 block rounded-lg bg-canvas px-3 py-2.5 text-center text-sm font-bold text-brand-deep transition-colors duration-150 hover:bg-brand-light" data-id="element-189" data-mp="2gxwrfsw3a94j">
                      See all everyday services
                    </Link>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {navLinks.map(link => <Link key={link.to} to={link.to} className={linkClass} data-id="element-190" data-mp="11gmym04pxw31">
              {link.label}
            </Link>)}
        </nav>

        <div className="hidden items-center gap-5 lg:flex" data-id="element-191" data-mp="15h6vd2ldqxon">
          <a href={site.phoneHref} className="hidden items-center gap-2 whitespace-nowrap font-display text-xl font-bold text-white xl:flex" data-id="element-192" data-mp="xyh1kjf1xbag">
            <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-193" data-mp="oykg0qnyksjm" />
            {site.phone}
          </a>
          <a href="#quote" className="whitespace-nowrap rounded-lg border-2 border-white bg-ink px-5 py-2.5 font-display text-lg font-bold text-white transition-colors duration-150 hover:bg-ink-soft" data-id="element-194" data-mp="k0sh3cowc65x">
            Get Your Free Quote
          </a>
        </div>

        <button type="button" onClick={() => setOpen(v => !v)} className="grid h-11 w-11 place-items-center rounded-lg text-white lg:hidden" aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? 'Close menu' : 'Open menu'} data-id="element-195" data-mp="5al43chnrfcm">
          {open ? <XIcon className="h-6 w-6" data-id="element-196" data-mp="3nhr5fyzdegq7" /> : <MenuIcon className="h-6 w-6" data-id="element-197" data-mp="3nhhlot4x55sy" />}
        </button>
      </div>

      <AnimatePresence data-id="element-198" data-mp="3dsaqg4dqojo8">
        {open && <motion.nav id="mobile-nav" aria-label="Mobile" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease
      }} className="max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-white/15 bg-brand-dark px-5 pb-6 pt-2 lg:hidden" data-id="element-199" data-mp="3dvgdle2t5bhb">
            <button type="button" onClick={() => setMobileServicesOpen(v => !v)} aria-expanded={mobileServicesOpen} className="flex w-full items-center justify-between border-b border-white/10 py-3 font-display text-xl font-semibold uppercase text-white" data-id="element-200" data-mp="5dakl1lhmhul">
              Services
              <ChevronDownIcon className={`h-5 w-5 transition-transform duration-150 ${mobileServicesOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-201" data-mp="jc9xjbzn18ax" />
            </button>
            {mobileServicesOpen && <ul className="grid gap-0.5 border-b border-white/10 py-2" data-id="element-202" data-mp="23v41ct2640uc">
                {serviceLinks.map(s => <li key={s.slug} data-id="element-203" data-mp="2ikibi3dkimas">
                    <Link to={`/${s.slug}`} className="block py-2 pl-3 font-semibold text-white/90" data-id="element-204" data-mp="2ijgyvugwlrqy">
                      {s.name}
                    </Link>
                  </li>)}
              </ul>}
            {navLinks.map(link => <Link key={link.to} to={link.to} className="block border-b border-white/10 py-3 font-display text-xl font-semibold uppercase text-white" data-id="element-205" data-mp="1kx1cce9me3wj">
                {link.label}
              </Link>)}
            <a href="#quote" onClick={() => setOpen(false)} className="mt-5 block rounded-lg bg-ink py-3 text-center font-display text-xl font-bold text-white" data-id="element-206" data-mp="3aawjxk2tb6f2">
              Get Your Free Quote
            </a>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}